import { z } from "zod";

// Zod 4 promotes string formats to tree-shakable top-level helpers
// (z.email / z.url / z.uuid / z.iso.datetime); the chained `z.string().email()`
// forms are deprecated. See https://zod.dev/v4/changelog

// User Auth Schemas
export const signInSchema = z.object({
  email: z.email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

// Category Schemas
export const categorySchema = z.object({
  name: z.string().min(1, "Name is required").max(100, "Name is too long"),
  slug: z
    .string()
    .min(1, "Slug is required")
    .max(100, "Slug is too long")
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      "Slug must be lowercase alphanumeric with hyphens only",
    ),
  description: z
    .string()
    .max(500, "Description is too long")
    .optional()
    .nullable(),
  color: z.string().optional().nullable(),
  icon: z.string().optional().nullable(),
  image: z.url("Image must be a valid URL").optional().nullable(),
});

// Article Schemas
export const articleSchema = z.object({
  title: z.string().min(1, "Title is required").max(300, "Title is too long"),
  slug: z
    .string()
    .min(1, "Slug is required")
    .max(200, "Slug is too long")
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      "Slug must be lowercase alphanumeric with hyphens only",
    ),
  excerpt: z
    .string()
    .min(1, "Excerpt is required")
    .max(500, "Excerpt is too long"),
  content: z.string().min(1, "Content is required"),
  // category_id is optional at the form level; DB constraint enforces existence when set
  category_id: z.uuid("Please select a valid category").optional().nullable(),
  author_name: z
    .string()
    .min(2, "Author name is required (min 2 chars)")
    .max(100, "Author name is too long"),
  reading_time: z
    .string()
    .max(20, "Reading time is too long")
    .optional()
    .nullable(),
  // featured_image is optional — articles can be published without one
  featured_image: z
    .url("Featured image must be a valid URL")
    .optional()
    .nullable(),
  // "archived" is not a valid DB CHECK value — only draft/published are allowed
  status: z.enum(["draft", "published"]).default("draft"),
  tags: z
    .array(z.string().max(50, "Tag is too long"))
    .max(20, "Too many tags")
    .default([]),
  scheduled_at: z.iso
    .datetime({ error: "scheduled_at must be a valid ISO datetime" })
    .optional()
    .nullable(),
  published_at: z.iso
    .datetime({ error: "published_at must be a valid ISO datetime" })
    .optional()
    .nullable(),
});

// Comment Schemas
export const commentSchema = z.object({
  article_id: z.uuid("Invalid article ID"),
  author_name: z
    .string()
    .min(1, "Name is required")
    .max(100, "Name is too long"),
  author_email: z.email("Invalid email address").max(254, "Email is too long"),
  body: z
    .string()
    .min(1, "Comment body is required")
    .max(5000, "Comment must be under 5000 characters"),
  status: z.enum(["approved", "pending", "rejected"]).default("pending"),
});

// Newsletter Subscriber Schemas
export const subscriberSchema = z.object({
  email: z.email("Invalid email address").max(254, "Email is too long"),
  source: z.string().max(100, "Source is too long").optional().nullable(),
});

// Inferred input types — consumers import these instead of re-deriving
// `z.infer<typeof …>` at every call site.
export type SignInInput = z.infer<typeof signInSchema>;
export type CategoryInput = z.infer<typeof categorySchema>;
export type ArticleInput = z.infer<typeof articleSchema>;
export type CommentInput = z.infer<typeof commentSchema>;
export type SubscriberInput = z.infer<typeof subscriberSchema>;
